import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { addBreadcrumb, captureException } from '../utils/sentry';
import { getDefaultLandingPath } from '../utils/defaultLandingPath';
import { consumePostLoginRedirect } from '../utils/postLoginRedirect';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
} from '@roger/ui-components';

const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [errorMessage, setErrorMessage] = useState('');
  const { checkAuthStatus } = useStore(
    useShallow(state => ({
      checkAuthStatus: state.checkAuthStatus,
    }))
  );
  const navigate = useNavigate();
  // Signing in is a once-per-mount side effect. StrictMode runs effects twice
  // in dev, which otherwise queues two redirects and lets the later one win.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const handleCallback = async () => {
      try {
        addBreadcrumb('Auth callback started', 'auth');

        // Check if user is authenticated (cookies should be set by backend)
        await checkAuthStatus();

        // Check if authentication was successful
        const isAuthenticated = useStore.getState().isAuthenticated;

        if (isAuthenticated) {
          addBreadcrumb('Auth callback successful', 'auth');
          setStatus('success');
          // A deep link the user opened while signed out wins over their
          // default landing page (EMR-1159).
          const landingPath =
            consumePostLoginRedirect() ??
            getDefaultLandingPath(useStore.getState());
          // Redirect after a brief delay so clinicians land on a page they can use.
          setTimeout(() => {
            navigate(landingPath, { replace: true });
          }, 1500);
        } else {
          addBreadcrumb(
            'Auth callback failed - user not authenticated',
            'auth'
          );
          setStatus('error');
          setErrorMessage('Authentication failed. Please try again.');
        }
      } catch (error) {
        addBreadcrumb('Auth callback error', 'auth', {
          error: error instanceof Error ? error.message : String(error),
        });
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            context: 'auth-callback',
          }
        );
        setStatus('error');
        setErrorMessage('An error occurred during authentication.');
      }
    };

    handleCallback();
  }, [checkAuthStatus, navigate]);

  return (
    <div className='min-h-screen bg-background flex items-center justify-center p-4'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center'>
          {/* Roger Logo */}
          <img
            src='https://cdn.prod.website-files.com/6707130d88e878564ebeead2/6707e3f0baed2cc2cc663543_Group%205301.svg'
            alt='Roger Logo'
            className='mx-auto mb-6 h-12 w-auto'
            style={{ maxHeight: 48 }}
          />

          <Typography variant='h3' as='h1' className='mb-2'>
            {status === 'loading' && 'Signing you in...'}
            {status === 'success' && 'Welcome to Roger Healthcare!'}
            {status === 'error' && 'Authentication Error'}
          </Typography>
        </CardHeader>

        <CardContent className='text-center'>
          {status === 'loading' && (
            <div className='space-y-4'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
              <Typography variant='p' className='text-muted-foreground'>
                Completing your sign-in...
              </Typography>
            </div>
          )}

          {status === 'success' && (
            <div className='space-y-4'>
              <div className='text-green-500 text-4xl'>✓</div>
              <Typography variant='p' className='text-muted-foreground'>
                Authentication successful! Redirecting...
              </Typography>
            </div>
          )}

          {status === 'error' && (
            <div className='space-y-4'>
              <div className='text-red-500 text-4xl'>✗</div>
              <Typography variant='p' className='text-destructive'>
                {errorMessage}
              </Typography>
              <button
                onClick={() => navigate('/login')}
                className='text-primary hover:underline'
              >
                Return to login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;
